import { create } from "zustand";
import { Furniture, FurnitureInstance } from "./Furniture";
import { createModelInstance } from "@/3d/modelInstance/ModelInstance";
import { v4 as uuidv4 } from "uuid";
import { MaterialResources } from "./MaterialResources";
import { AABB, getModelInstanceAabb } from "@/3d/movement2d/aabb2d";
import {
    filterRoomFractionsBySize,
    fixAabbPositionByRoomFractions,
    fractureRoom,
} from "@/3d/movement2d/aabb2dIntersections";
import { Color } from "three";
import { isNotNil } from "@/utils/typeGuards";
import { loadModelWithConfig } from "@/api/model";

type FurnitureManagementState = {
    furnitureInstances: Record<string, FurnitureInstance>;
    freeSpace: AABB[];
    loadingFurnitureIds: string[];

    createFurnitureInstance: (
        furniture: Furniture,
        materials: Record<string, MaterialResources>,
        position?: { x: number; y: number },
        rotation?: number
    ) => Promise<FurnitureInstance | undefined>;
    deleteFurnitureInstance: (instanceId: string) => void;
    setFreeSpace: (freeSpace: AABB[]) => void;
    updateFreeSpace: (
        roomAabb: AABB,
        filterFurniture?: (furnitureInstance: FurnitureInstance) => boolean
    ) => AABB[];
    setMaterialSlotMaterialName: (instanceId: string, slotId: string, materialName: string) => void;
    setMaterialSlotMaterialColor: (instanceId: string, slotId: string, color: Color) => void;
    updateFurnitureInstancePosition: (
        instanceId: string,
        position: { x: number; y: number },
        rotation: number
    ) => void;
};

export const useFurnitureManagementState = create<FurnitureManagementState>((set, get) => ({
    furnitureInstances: {},
    freeSpace: [],
    loadingFurnitureIds: [],

    createFurnitureInstance: async (
        furniture: Furniture,
        materials: Record<string, MaterialResources>,
        position?: { x: number; y: number },
        rotation?: number
    ) => {
        let model = furniture.model;
        if (!model) {
            set((state) => ({
                loadingFurnitureIds: [...state.loadingFurnitureIds, furniture.id],
            }));
            model = await loadModelWithConfig({
                modelPath: furniture.resources.modelPath,
                configPath: furniture.resources.configPath,
            });

            if (!model) {
                console.error("failed to load model for furniture", furniture.data.code);
                set((state) => ({
                    loadingFurnitureIds: state.loadingFurnitureIds.filter(
                        (id) => id !== furniture.id
                    ),
                }));
                return undefined;
            }

            furniture.model = model;
            set((state) => ({
                loadingFurnitureIds: state.loadingFurnitureIds.filter((id) => id !== furniture.id),
            }));
        }

        const instance = createModelInstance(
            model.raw,
            model.config,
            model.raw.meshStats,
            materials
        );

        const aabb = getModelInstanceAabb({
            box: instance.box,
            position: position ?? instance.position,
            rotation: rotation ?? instance.rotation,
        });
        const freeSpace = get().freeSpace;
        const filteredFreeSpace = filterRoomFractionsBySize(freeSpace, aabb.width, aabb.height);
        const fixedAabb = fixAabbPositionByRoomFractions(aabb, filteredFreeSpace);

        if (!fixedAabb) {
            console.error("No suitable space found");
            return undefined;
        }

        instance.position = {
            x: fixedAabb.minX,
            y: fixedAabb.minY,
        };
        instance.rotation = rotation ?? instance.rotation;

        const furnitureInstance: FurnitureInstance = {
            id: uuidv4(),
            furnitureId: furniture.id,
            instance,
            type: "furniture",
        };

        set({
            furnitureInstances: {
                ...get().furnitureInstances,
                [furnitureInstance.id]: furnitureInstance,
            },
        });

        return furnitureInstance;
    },

    setFreeSpace: (freeSpace: AABB[]) => {
        set({ freeSpace });
    },

    setMaterialSlotMaterialName: (instanceId: string, slotId: string, materialName: string) => {
        const instance = get().furnitureInstances[instanceId].instance;
        if (!instance) {
            console.error("instance not found");
            return;
        }

        set({
            furnitureInstances: {
                ...get().furnitureInstances,
                [instanceId]: {
                    ...get().furnitureInstances[instanceId],
                    instance: {
                        ...instance,
                        materialSlotMapping: {
                            ...instance.materialSlotMapping,
                            [slotId]: {
                                ...instance.materialSlotMapping[slotId],
                                materialName,
                            },
                        },
                    },
                },
            },
        });
    },
    setMaterialSlotMaterialColor: (instanceId: string, slotId: string, color: Color) => {
        const instance = get().furnitureInstances[instanceId].instance;
        if (!instance) {
            console.error("instance not found");
            return;
        }

        set({
            furnitureInstances: {
                ...get().furnitureInstances,
                [instanceId]: {
                    ...get().furnitureInstances[instanceId],
                    instance: {
                        ...instance,
                        materialSlotMapping: {
                            ...instance.materialSlotMapping,
                            [slotId]: {
                                ...instance.materialSlotMapping[slotId],
                                color,
                            },
                        },
                    },
                },
            },
        });
    },
    deleteFurnitureInstance: (instanceId: string) => {
        const updatedFurnitureInstances = { ...get().furnitureInstances };
        delete updatedFurnitureInstances[instanceId];

        set({
            furnitureInstances: updatedFurnitureInstances,
        });
    },
    updateFurnitureInstancePosition: (
        instanceId: string,
        position: { x: number; y: number },
        rotation: number
    ) => {
        const allInstances = get().furnitureInstances;
        const instance = allInstances[instanceId];
        if (!instance) {
            console.error("instance not found");
            return;
        }

        const updatedInstance = {
            ...instance,
            instance: {
                ...instance.instance,
                position: { x: position.x, y: position.y },
                rotation: rotation,
            },
        };

        set({
            furnitureInstances: {
                ...allInstances,
                [instanceId]: updatedInstance,
            },
        });
    },
    updateFreeSpace: (
        roomAabb: AABB,
        filterFurniture?: (furnitureInstance: FurnitureInstance) => boolean
    ) => {
        const furnitureInstances = get().furnitureInstances;
        const filteredFurnitureInstances = isNotNil(filterFurniture)
            ? Object.values(furnitureInstances).filter(filterFurniture)
            : Object.values(furnitureInstances);

        if (filteredFurnitureInstances.length === 0) {
            get().setFreeSpace([roomAabb]);

            return [roomAabb];
        }

        const furnitureAabbs = filteredFurnitureInstances.map((instance) =>
            getModelInstanceAabb(instance.instance)
        );
        const fractions = fractureRoom(roomAabb, furnitureAabbs);

        get().setFreeSpace(fractions);

        return fractions;
    },
}));
